/**
 * Public-facing NOXN contract view.
 * Layout inspired by the "PAID.03 INVOICE COMPLETE" reference template,
 * re-skinned with NOXN's navy + orange + white palette. Fields that the
 * client still needs to fill (ABN, address, phone) are fully editable inputs
 * before signing; everything else is a snapshot with an in-page PDF download.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Download, Check, Loader2, ShieldCheck, PenTool, MapPin } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import "./contract.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (cents) => `$${((cents || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ContractPortal() {
    const { token } = useParams();
    usePageMeta({ title: "NOXN Contract", description: "Review and sign your NOXN campaign contract." });

    const [state, setState] = useState({ loading: true, contract: null, error: null });
    const [edits, setEdits] = useState({ abn: "", address: "", phone: "" });
    const [fullName, setFullName] = useState("");
    const [agree, setAgree] = useState(false);
    const [signing, setSigning] = useState(false);

    const load = useCallback(async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
            const { data } = await axios.get(`${API}/contracts/public/${token}`);
            setState({ loading: false, contract: data, error: null });
            setEdits({ abn: data.abn || "", address: data.address || "", phone: data.phone || "" });
        } catch (e) {
            setState({ loading: false, contract: null, error: e?.response?.data?.detail || "Could not load this contract." });
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const c = state.contract;
    const isSigned = c?.status === "signed";

    const sign = async (e) => {
        e?.preventDefault?.();
        if (!fullName.trim() || fullName.trim().length < 2) {
            toast.error("Please enter your full legal name."); return;
        }
        if (!agree) {
            toast.error("You must agree to the terms."); return;
        }
        setSigning(true);
        try {
            await axios.post(`${API}/contracts/public/${token}/sign`, {
                full_name: fullName.trim(),
                agree: true,
                client_edits: edits,
            });
            toast.success("Contract signed.");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not record your signature.");
        } finally { setSigning(false); }
    };

    if (state.loading) {
        return (
            <div className="min-h-screen bg-[#F5F7FB] flex items-center justify-center">
                <Loader2 className="animate-spin text-[#FF5A00]" size={22} />
            </div>
        );
    }
    if (state.error || !c) {
        return (
            <div className="min-h-screen bg-[#F5F7FB] flex items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Error</div>
                    <div className="mt-2 text-[#0B1E3E] text-lg font-display font-black">{state.error || "Contract unavailable."}</div>
                    <p className="mt-3 text-sm text-slate-500">If you believe this is a mistake, please contact NOXN directly.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="contract-portal min-h-screen bg-[#F5F7FB] py-10">
            <div className="mx-auto max-w-[860px] px-4 sm:px-6">
                <ActionBar contract={c} token={token} />
                <motion.div
                    initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, ease: [0.16,1,0.3,1] }}
                    className="contract-doc"
                    data-testid="contract-doc"
                >
                    {/* Header */}
                    <div className="contract-header">
                        <div>
                            <img src="/brand/noxn-secondary-navy.png" alt="NOXN" className="h-7 w-auto mb-3 select-none" draggable="false" />
                            <div className="kicker">CAMPAIGN CONTRACT · TAX INVOICE</div>
                            <h1 className="contract-title">{c.contract_number}</h1>
                            <p className="tagline">Own the streets. Own the market.</p>
                        </div>
                        <div className="header-meta">
                            <div><span className="field-label">Status</span><span className={`status-pill status-${c.status}`}>{c.status}</span></div>
                            <div><span className="field-label">Issued</span><span className="field-value">{c.issue_date}</span></div>
                        </div>
                    </div>

                    {/* Bill To / Details */}
                    <section className="contract-section">
                        <div className="two-col">
                            <div>
                                <div className="section-heading">Bill To</div>
                                <StaticField label="Client Name" value={c.client_name} />
                                <StaticField label="Business Name" value={c.business_name} />
                                {isSigned ? (
                                    <>
                                        <StaticField label="ABN / Business Number" value={c.abn} />
                                        <StaticField label="Address" value={c.address} />
                                        <StaticField label="Phone" value={c.phone} />
                                    </>
                                ) : (
                                    <>
                                        <EditableField label="ABN / Business Number" placeholder="e.g. 12 345 678 901"
                                            value={edits.abn} onChange={(v) => setEdits((s) => ({ ...s, abn: v }))} testid="contract-abn" />
                                        <EditableField label="Address" placeholder="Street, suburb, state, postcode"
                                            value={edits.address} onChange={(v) => setEdits((s) => ({ ...s, address: v }))} testid="contract-address" />
                                        <EditableField label="Phone" placeholder="04XX XXX XXX"
                                            value={edits.phone} onChange={(v) => setEdits((s) => ({ ...s, phone: v }))} testid="contract-phone" />
                                    </>
                                )}
                                <StaticField label="Email" value={c.email} />
                            </div>
                            <div>
                                <div className="section-heading">Contract Details</div>
                                <StaticField label="Issue Date" value={c.issue_date} />
                                <StaticField label="Due Date" value={c.due_date || "On booking (paid)"} />
                                <StaticField label="Run Window" value={`${c.run_start || "—"} → ${c.run_end || "—"}`} />
                                <StaticField label="Payment Terms" value={c.payment_terms} />
                                <StaticField label="Campaign Reference" value={c.campaign_id?.slice(0, 8)} />
                            </div>
                        </div>
                    </section>

                    {/* Campaign scope */}
                    <section className="contract-section">
                        <div className="section-heading">Campaign Scope</div>
                        <div className="scope-card">
                            <div><span className="field-label">Goal</span><span className="field-value">{(c.campaign_goal || "—").replace(/_/g, " ")}</span></div>
                            <div><span className="field-label">Category</span><span className="field-value">{c.campaign_category === "cat1" ? "Category 1" : c.campaign_category === "cat2" ? "Category 2" : "—"}</span></div>
                            <div className="col-span-2"><span className="field-label">Cities</span>
                                <span className="field-value inline-flex flex-wrap gap-1.5">
                                    {(c.cities || []).length ? c.cities.map((ct) => (
                                        <span key={ct} className="chip"><MapPin size={10} /> {ct}</span>
                                    )) : "—"}
                                </span>
                            </div>
                        </div>
                    </section>

                    {/* Line items */}
                    <section className="contract-section">
                        <div className="section-heading">Screens &amp; Services</div>
                        <table className="line-items" data-testid="contract-line-items">
                            <thead>
                                <tr>
                                    <th>Type</th><th>Description</th>
                                    <th className="num">Qty</th><th className="num">Rate</th><th className="num">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(c.line_items || []).length === 0 ? (
                                    <tr><td colSpan="5" className="empty">No screens attached to this campaign yet.</td></tr>
                                ) : (c.line_items || []).map((li, i) => (
                                    <tr key={i}>
                                        <td><span className="kind-tag">{li.kind}</span></td>
                                        <td className="desc">{li.description}</td>
                                        <td className="num">{li.qty}</td>
                                        <td className="num">{money(li.rate_cents)}</td>
                                        <td className="num strong">{money(li.amount_cents)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="totals">
                            <div className="totals-row"><span>Subtotal</span><span>{money(c.subtotal_cents)}</span></div>
                            {c.discount_cents ? <div className="totals-row"><span>Discount</span><span>−{money(c.discount_cents)}</span></div> : null}
                            <div className="totals-row"><span>GST (10%)</span><span>{money(c.gst_cents)}</span></div>
                            {c.referral_discount_cents ? <div className="totals-row" data-testid="contract-referral-discount"><span>Referral discount</span><span>−{money(c.referral_discount_cents)}</span></div> : null}
                            {c.credit_applied_cents ? <div className="totals-row" data-testid="contract-credit-applied"><span>Account credit applied</span><span>−{money(c.credit_applied_cents)}</span></div> : null}
                            <div className="totals-row final"><span>Total Due</span><span>{money(c.total_cents)}</span></div>
                        </div>
                    </section>

                    {/* Notes */}
                    {c.notes ? (
                        <section className="contract-section">
                            <div className="section-heading">Notes</div>
                            <p className="notes-body">{c.notes}</p>
                        </section>
                    ) : null}

                    {/* Terms */}
                    <section className="contract-section terms-section">
                        <div className="section-heading">Terms &amp; Conditions</div>
                        <div className="terms-body">
                            {(c.terms || "").split("\n").filter((l) => l.trim()).map((l, i) => (
                                <p key={i}>{l}</p>
                            ))}
                        </div>
                    </section>

                    {/* Signature */}
                    <section className="contract-section signature-section" data-testid="contract-signature-section">
                        <div className="section-heading">Signature</div>
                        {isSigned && c.signature ? (
                            <div className="signed-block" data-testid="contract-signed-block">
                                <div className="signed-name">{c.signature.full_name}</div>
                                <div className="signed-meta">
                                    <span><ShieldCheck size={12} /> Signed {new Date(c.signature.signed_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>
                                    <span>IP: {c.signature.ip || "—"}</span>
                                    <span>Hash: {c.signature.agreement_hash?.slice(0, 20)}…</span>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={sign} className="sign-form">
                                <label className="sign-field">
                                    <span className="field-label">Type your full legal name</span>
                                    <input
                                        type="text"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        data-testid="contract-sign-name"
                                        placeholder="e.g. Alex Morgan"
                                        autoComplete="off"
                                        required
                                    />
                                </label>
                                <label className="agree-row" data-testid="contract-agree-label">
                                    <input
                                        type="checkbox"
                                        checked={agree}
                                        onChange={(e) => setAgree(e.target.checked)}
                                        data-testid="contract-agree"
                                    />
                                    <span>I have read and agree to the terms above. I understand this signature is legally binding.</span>
                                </label>
                                <button type="submit" disabled={signing || !fullName.trim() || !agree}
                                    data-testid="contract-sign-submit"
                                    className="sign-submit">
                                    {signing ? (<><Loader2 className="animate-spin" size={14} /> Signing…</>) : (<><PenTool size={14} /> Sign contract</>)}
                                </button>
                            </form>
                        )}
                    </section>

                    <p className="footer-line">Thank you for booking with NOXN.</p>
                </motion.div>
            </div>

            {/* Success flash */}
            <AnimatePresence>
                {isSigned && (
                    <motion.div
                        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                        className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#0B1E3E] text-white px-5 py-3 border border-[#FF5A00]/50 shadow-2xl"
                        data-testid="contract-signed-toast"
                    >
                        <div className="inline-flex items-center gap-2 text-sm">
                            <Check size={14} className="text-[#FF5A00]" /> Contract signed — a copy is on the way.
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

const ActionBar = ({ contract, token }) => (
    <div className="flex items-center justify-between mb-4">
        <div className="kicker">
            <span className="w-6 h-px bg-[#FF5A00] inline-block mr-2 align-middle" />
            {contract.contract_number}
        </div>
        <a
            href={`${API}/contracts/public/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contract-download-pdf"
            className="inline-flex items-center gap-2 bg-[#0B1E3E] hover:bg-[#0F1E3B] text-white px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-colors"
        >
            <Download size={12} /> PDF
        </a>
    </div>
);

const StaticField = ({ label, value }) => (
    <div className="field">
        <div className="field-label">{label}</div>
        <div className="field-value">{value || "—"}</div>
    </div>
);

const EditableField = ({ label, value, onChange, placeholder, testid }) => (
    <div className="field">
        <div className="field-label">{label}</div>
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="field-input"
            data-testid={testid}
        />
    </div>
);
